<template>
  <div class="flex">
    <div class="flex-2">
      <div class="block">
        <div class="inline-flex bg-gray-200">
          <span class="inline-grid grid-cols-1 col-gap-4 bg-gray-400" v-for="(column, index) in columns" :key="'takeButton_' + index">
            <button class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 m-2 w-16 rounded" :class="buttonDisabled(columnComplete(column))" v-on:click="toggleColumnTaken(column)">
              X
            </button>
          </span>
        </div>
      </div>
      <div class="block">
        <div class="inline-flex bg-gray-200">
          <span class="inline-grid grid-cols-1 col-gap-4 bg-gray-400" v-for="(column, index) in columns" :key="'unclaimButton_' + index">
            <button class="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 m-2 w-16 rounded" :class="buttonDisabled(!columnActive(column))" :disabled="!columnActive(column)" v-on:click="removeMarker(column)">
              -
            </button>
          </span>
        </div>
      </div>
      <div class="block">
        <div class="inline-flex bg-gray-200">
          <span class="inline-grid grid-cols-1 col-gap-4 bg-gray-400" v-for="(column, i) in columns" :key="'column_' + i">
            <div class="text-center bg-gray-600 px-4 py-2 m-2 w-16" :class="availablePointsColour(column)" v-for="(available, j) in availableSpaces(column)" :key="column.columnId + '_available_' + available">
              {{ availablePoints(column, j) }}
            </div>
            <div class="text-green-800 text-center bg-green-500 px-4 py-2 m-2 w-16" v-for="(claimed, j) in claimedSpaces(column)" :key="column.columnId + '_claimed_' + claimed">
              {{ j != column.columnClaimedSpaces - 1 ? column.columnPoints : column.columnPoints * 2 }}
            </div>
            <div class="text-green-800 text-center bg-green-800 px-4 py-2 m-2 w-16" v-for="owned in ownedSpaces(column)" :key="column.columnId + 'owned_' + owned">•</div>
            <div class="text-red-500 text-center bg-red-500 px-4 py-2 m-2 w-16" v-for="taken in takenSpaces(column)" :key="column.columnId + 'taken_' + taken">•</div>
          </span>
        </div>
      </div>
      <div class="block">
        <div class="inline-flex bg-gray-200">
          <span class="inline-grid grid-cols-1 col-gap-4 bg-gray-400" v-for="(column, index) in columns" :key="'claimButton_' + index">
            <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 m-2 w-16 rounded" :class="buttonDisabled(columnUnavailable(column))" :disabled="columnUnavailable(column)" v-on:click="placeMarker(column)">
              {{ column.columnId }}
            </button>
          </span>
        </div>
      </div>
    </div>
    <div class="flex-1">
      <div class="grid grid-flow-row grid-cols-2 grid-rows-4 gap-4 text-center">
        <div class="border border-gray-400 bg-white rounded p-4 m-4 w-48 ml-auto">
          <div class="text-gray-900 font-bold text-xl mb-2">Current Score</div>
          <div class="relative">
            <p class="font-bold text-5xl" :class="scoreColour" >{{ currentScore }}</p>
          </div>
          <div class="relative -mt-4 -mb-4">
            <span v-if="allHighPenalty" class="mx-1 bg-orange-200 text-orange-800 text-xs px-2 rounded-full uppercase font-semibold tracking-wide">High +4</span>
            <span v-if="allLowPenalty" class="mx-1 bg-orange-200 text-orange-800 text-xs px-2 rounded-full uppercase font-semibold tracking-wide">Low +4</span>
            <span v-if="allOddPenalty" class="mx-1 bg-orange-200 text-orange-800 text-xs px-2 rounded-full uppercase font-semibold tracking-wide">Odd +2</span>
            <span v-if="allEvenBonus" class="mx-1 bg-green-200 text-green-800 text-xs px-2 rounded-full uppercase font-semibold tracking-wide">Even -2</span>
          </div>
        </div>
        <div class="border border-gray-400 bg-white rounded p-4 m-4 w-48 mr-auto">
          <div class="text-gray-900 font-bold text-xl mb-2">Markers Left</div>
          <p class="text-gray-900 font-bold text-5xl">{{ remainingMarkers }}</p>
        </div>
        <div class="border border-gray-400 bg-white rounded p-4 m-4 w-48 ml-auto">
          <div class="text-gray-900 font-bold text-xl mb-2">Allowed</div>
          <p class="text-gray-900 font-bold text-2xl">{{ possibleColumns.join(', ') }}</p>
        </div>
        <div class="border border-gray-400 bg-white rounded p-4 m-4 w-48 mr-auto">
          <div class="text-gray-900 font-bold text-xl mb-2">Wipeout Chance</div>
          <p class="font-bold text-5xl" :class="riskColour">{{ (wipeoutProbability * 100).toFixed(2) + '%' }}</p>
        </div>
        <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold p-4 m-4 w-48 ml-auto rounded" v-on:click="saveProgress()">
          Save
        </button>
        <button class="bg-red-500 hover:bg-red-700 text-white font-bold p-4 m-4 w-48 mr-auto rounded" v-on:click="wipeout()">
          Wipe Out
        </button>
        <div class="text-white font-bold p-4 m-4 w-48 ml-auto">
          Spacer
        </div>
        <button class="bg-red-500 hover:bg-red-700 text-white font-bold p-4 m-4 w-48 mr-auto rounded" v-on:click="resetBoard()">
          Reset
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      columns: [
        {
          columnId: 2,
          columnSpaces: 3,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 6
        },
        {
          columnId: 3,
          columnSpaces: 5,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 5
        },
        {
          columnId: 4,
          columnSpaces: 7,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 4
        },
        {
          columnId: 5,
          columnSpaces: 9,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 3
        },
        {
          columnId: 6,
          columnSpaces: 11,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 2
        },
        {
          columnId: 7,
          columnSpaces: 13,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 1
        },
        {
          columnId: 8,
          columnSpaces: 11,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 2
        },
        {
          columnId: 9,
          columnSpaces: 9,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 3
        },
        {
          columnId: 10,
          columnSpaces: 7,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 4
        },
        {
          columnId: 11,
          columnSpaces: 5,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 5
        },
        {
          columnId: 12,
          columnSpaces: 3,
          columnTaken: false,
          columnClaimedSpaces: 0,
          columnOwnedSpaces: 0,
          columnPoints: 6
        },
      ],
      moveChances: [
        { moves: [2],             chances: 1,  combinations: [[1,1,1,1]] },
        { moves: [2,3],           chances: 4,  combinations: [[1,1,1,2]] },
        { moves: [2,3,4],         chances: 6,  combinations: [[1,1,2,2]] },
        { moves: [2,3,4,5],       chances: 12, combinations: [[1,1,2,3]] },
        { moves: [2,3,5,6],       chances: 12, combinations: [[1,1,2,4]] },
        { moves: [2,3,6,7],       chances: 12, combinations: [[1,1,2,5]] },
        { moves: [2,3,7,8],       chances: 12, combinations: [[1,1,2,6]] },
        { moves: [2,4],           chances: 4,  combinations: [[1,1,1,3]] },
        { moves: [2,4,5,7],       chances: 12, combinations: [[1,1,3,4]] },
        { moves: [2,4,6],         chances: 6,  combinations: [[1,1,3,3]] },
        { moves: [2,4,6,8],       chances: 12, combinations: [[1,1,3,5]] },
        { moves: [2,4,7,9],       chances: 12, combinations: [[1,1,3,6]] },
        { moves: [2,5],           chances: 4,  combinations: [[1,1,1,4]] },
        { moves: [2,5,6,9],       chances: 12, combinations: [[1,1,4,5]] },
        { moves: [2,5,7,10],      chances: 12, combinations: [[1,1,4,6]] },
        { moves: [2,5,8],         chances: 6,  combinations: [[1,1,4,4]] },
        { moves: [2,6],           chances: 4,  combinations: [[1,1,1,5]] },
        { moves: [2,6,10],        chances: 6,  combinations: [[1,1,5,5]] },
        { moves: [2,6,7,11],      chances: 12, combinations: [[1,1,5,6]] },
        { moves: [2,7],           chances: 4,  combinations: [[1,1,1,6]] },
        { moves: [2,7,12],        chances: 6,  combinations: [[1,1,6,6]] },
        { moves: [3,4],           chances: 4,  combinations: [[1,2,2,2]] },
        { moves: [3,4,5],         chances: 12, combinations: [[1,2,2,3]] },
        { moves: [3,4,5,6],       chances: 24, combinations: [[1,2,2,4], [1,2,3,6]] },
        { moves: [3,4,5,6,7],     chances: 24, combinations: [[1,2,3,4]] },
        { moves: [3,4,5,6,7,8],   chances: 24, combinations: [[1,2,3,5]] },
        { moves: [3,4,5,7,8,9],   chances: 24, combinations: [[1,2,3,6]] },
        { moves: [3,4,6,7],       chances: 12, combinations: [[1,2,2,5]] },
        { moves: [3,4,7,8],       chances: 12, combinations: [[1,2,2,6]] },
        { moves: [3,5,6,7,8,10],  chances: 24, combinations: [[1,2,4,6]] },
        { moves: [3,5,6,7,9],     chances: 24, combinations: [[1,2,4,5]] },
        { moves: [3,5,6,8],       chances: 12, combinations: [[1,2,4,4]] },
        { moves: [3,6,7,10],      chances: 12, combinations: [[1,2,5,5]] },
        { moves: [3,6,7,8,11],    chances: 24, combinations: [[1,2,5,6]] },
        { moves: [3,7,8,12],      chances: 12, combinations: [[1,2,6,6]] },
        { moves: [4],             chances: 1,  combinations: [[2,2,2,2]] },
        { moves: [4,5],           chances: 4,  combinations: [[2,2,2,3]] },
        { moves: [4,5,6],         chances: 6,  combinations: [[2,2,3,3]] },
        { moves: [4,5,6,7],       chances: 24, combinations: [[1,3,3,4], [2,2,3,4]] },
        { moves: [4,5,6,7,8,9],   chances: 24, combinations: [[1,3,4,5]] },
        { moves: [4,5,7,8],       chances: 24, combinations: [[1,3,4,4], [2,2,3,5]] },
        { moves: [4,5,7,9,10],    chances: 24, combinations: [[1,3,4,6]] },
        { moves: [4,5,8,9],       chances: 12, combinations: [[2,2,3,6]] },
        { moves: [4,6],           chances: 8,  combinations: [[1,3,3,3], [2,2,2,4]] },
        { moves: [4,6,7,8,9,11],  chances: 24, combinations: [[1,3,5,6]] },
        { moves: [4,6,7,9],       chances: 24, combinations: [[1,3,3,6], [2,2,4,5]] },
        { moves: [4,6,8],         chances: 18, combinations: [[1,3,3,5], [2,2,4,4]] },
        { moves: [4,6,8,10],      chances: 24, combinations: [[1,3,5,5], [2,2,4,6]] },
        { moves: [4,7],           chances: 4,  combinations: [[2,2,2,5]] },
        { moves: [4,7,8,11],      chances: 12, combinations: [[2,2,5,6]] },
        { moves: [4,7,9,12],      chances: 12, combinations: [[1,3,6,6]] },
        { moves: [4,7,10],        chances: 6,  combinations: [[2,2,5,5]] },
        { moves: [4,8],           chances: 4,  combinations: [[2,2,2,6]] },
        { moves: [4,8,12],        chances: 6,  combinations: [[2,2,6,6]] },
        { moves: [5,6],           chances: 4,  combinations: [[2,3,3,3]] },
        { moves: [5,6,7,8],       chances: 24, combinations: [[2,3,3,5], [2,3,4,4]] },
        { moves: [5,6,7,8,9],     chances: 24, combinations: [[2,3,4,5]] },
        { moves: [5,6,7,8,9,10],  chances: 24, combinations: [[2,3,4,6]] },
        { moves: [5,6,7,9,10,11], chances: 24, combinations: [[1,4,5,6]] },
        { moves: [5,6,8,9],       chances: 24, combinations: [[1,4,4,5], [2,3,3,6]] },
        { moves: [5,6,9,10],      chances: 12, combinations: [[1,4,5,5]] },
        { moves: [5,7],           chances: 12, combinations: [[2,3,3,4]] },
        { moves: [5,7,8,10],      chances: 24, combinations: [[1,4,4,6], [2,3,5,5]] },
        { moves: [5,7,8,9,11],    chances: 24, combinations: [[2,3,5,6]] },
        { moves: [5,7,10,12],     chances: 12, combinations: [[1,4,6,6]] },
        { moves: [5,8],           chances: 4,  combinations: [[1,4,4,4]] },
        { moves: [5,8,9,12],      chances: 12, combinations: [[2,3,6,6]] },
        { moves: [6],             chances: 1,  combinations: [[3,3,3,3]] },
        { moves: [6,7],           chances: 4,  combinations: [[3,3,3,4]] },
        { moves: [6,7,8],         chances: 6,  combinations: [[3,3,4,4]] },
        { moves: [6,7,8,9],       chances: 24, combinations: [[2,4,4,5], [3,3,4,5]] },
        { moves: [6,7,8,9,10,11], chances: 24, combinations: [[2,4,5,6]] },
        { moves: [6,7,9,10],      chances: 24, combinations: [[2,4,5,5], [3,3,4,6]] },
        { moves: [6,7,10,11],     chances: 12, combinations: [[1,5,5,6]] },
        { moves: [6,7,11,12],     chances: 12, combinations: [[1,5,6,6]] },
        { moves: [6,8],           chances: 8,  combinations: [[2,4,4,4], [3,3,3,5]] },
        { moves: [6,8,9,11],      chances: 12, combinations: [[3,3,5,6]] },
        { moves: [6,8,10],        chances: 18, combinations: [[2,4,4,6], [3,3,5,5]] },
        { moves: [6,8,10,12],     chances: 12, combinations: [[2,4,6,6]] },
        { moves: [6,9],           chances: 4,  combinations: [[3,3,3,6]] },
        { moves: [6,9,12],        chances: 6,  combinations: [[3,3,6,6]] },
        { moves: [6,10],          chances: 4,  combinations: [[1,5,5,5]] },
        { moves: [7,8],           chances: 4,  combinations: [[3,4,4,4]] },
        { moves: [7,8,9],         chances: 12, combinations: [[3,4,4,5]] },
        { moves: [7,8,9,10],      chances: 24, combinations: [[3,4,4,6], [3,4,5,5]] },
        { moves: [7,8,9,10,11],   chances: 24, combinations: [[3,4,5,6]] },
        { moves: [7,8,10,11],     chances: 12, combinations: [[2,5,5,6]] },
        { moves: [7,8,11,12],     chances: 12, combinations: [[2,5,6,6]] },
        { moves: [7,9,10,12],     chances: 12, combinations: [[3,4,6,6]] },
        { moves: [7,10],          chances: 4,  combinations: [[2,5,5,5]] },
        { moves: [7,12],          chances: 4,  combinations: [[1,6,6,6]] },
        { moves: [8],             chances: 1,  combinations: [[4,4,4,4]] },
        { moves: [8,9],           chances: 4,  combinations: [[4,4,4,5]] },
        { moves: [8,9,10],        chances: 6,  combinations: [[4,4,5,5]] },
        { moves: [8,9,10,11],     chances: 24, combinations: [[3,5,5,6], [4,4,5,6]] },
        { moves: [8,9,11,12],     chances: 12, combinations: [[3,5,6,6]] },
        { moves: [8,10],          chances: 8,  combinations: [[3,5,5,5], [4,4,4,6]] },
        { moves: [8,10,12],       chances: 6,  combinations: [[4,4,6,6]] },
        { moves: [8,12],          chances: 4,  combinations: [[2,6,6,6]] },
        { moves: [9,10],          chances: 4,  combinations: [[4,5,5,5]] },
        { moves: [9,10,11],       chances: 12, combinations: [[4,5,5,6]] },
        { moves: [9,10,11,12],    chances: 12, combinations: [[4,5,6,6]] },
        { moves: [9,12],          chances: 4,  combinations: [[3,6,6,6]] },
        { moves: [10],            chances: 1,  combinations: [[5,5,5,5]] },
        { moves: [10,11],         chances: 4,  combinations: [[5,5,5,6]] },
        { moves: [10,11,12],      chances: 6,  combinations: [[5,5,6,6]] },
        { moves: [10,12],         chances: 4,  combinations: [[4,6,6,6]] },
        { moves: [11,12],         chances: 4,  combinations: [[5,6,6,6]] },
        { moves: [12],            chances: 1,  combinations: [[6,6,6,6]] },
      ]
    }
  },
  computed: {
    activeColumns: function() {
      return this.columns.filter(column => this.columnActive(column))
    },
    allEvenBonus: function() {
      return this.activeColumns.filter(column => column.columnId % 2 == 0).length == 3 ? 2 : 0
    },
    allHighPenalty: function() {
      return this.activeColumns.filter(column => column.columnId > 6).length == 3 ? 4 : 0
    },
    allLowPenalty: function() {
      return this.activeColumns.filter(column => column.columnId < 8).length == 3 ? 4 : 0
    },
    allOddPenalty: function() {
      return this.activeColumns.filter(column => column.columnId % 2 != 0).length == 3 ? 2 : 0
    },
    completedColumns: function() {
      return this.columns.filter(column => (this.availableSpaces(column) == 0 && !column.columnTaken))
    },
    currentScore: function() {
      let columnsScore = this.activeColumns.reduce((tally,column) =>  tally + this.columnScore(column), 0)
      if (this.activeColumns.length < 3) {
        return columnsScore
      }
      
      return columnsScore + this.allOddPenalty - this.allEvenBonus + this.allHighPenalty + this.allLowPenalty
    },
    possibleColumns: function() {
      const availableColumns = this.columns.filter(column => !(this.columnComplete(column) || column.columnTaken))

      if (this.remainingMarkers < 1) {
        return this.activeColumns.filter(column => (availableColumns.includes(column))).map(column => column.columnId)
      }

      return availableColumns.map(column => column.columnId)
    },
    remainingMarkers: function() {
      return 3 - this.activeColumns.length
    },
    riskColour: function() {
      return "text-gray-900"
    },
    scoreColour: function() {
      let scoreRisk = this.currentScore >= 28
      let scoreHighRisk = this.currentScore >= 32
      let columnRisk = this.completedColumns.length > 0

      if (scoreHighRisk || (scoreRisk && columnRisk)) {
        return "text-red-600"
      } else if (scoreRisk || columnRisk) {
        return "text-orange-600"
      } else {
        return "text-green-600"
      }
    },
    wipeoutProbability: function() {
      if (this.possibleColumns.length == 11) {
        return 0
      }

      var intersection = []
      const wipeoutChances = this.moveChances.reduce( (tally, moveChance) => {
        intersection = moveChance.moves.filter(x => this.possibleColumns.includes(x))
        if (intersection.length == 0) {
          return tally + moveChance.chances
        }
        return tally
      }, 0)

      return wipeoutChances / 1296
    },
  },
  methods: {
    takenSpaces: function(column) {
      return column.columnTaken ? column.columnSpaces : 0
    },
    availableSpaces: function(column) {
      return column.columnTaken ? 0 : column.columnSpaces - column.columnClaimedSpaces - column.columnOwnedSpaces
    },
    claimedSpaces: function(column) {
      return column.columnTaken ? 0 : column.columnClaimedSpaces
    },
    ownedSpaces: function(column) {
      return column.columnTaken ? 0 : column.columnOwnedSpaces
    },
    columnUnavailable: function(column) {
      return (column.columnTaken || this.availableSpaces(column) == 0 || (this.remainingMarkers == 0 && !(this.columnActive(column))))
    },
    columnComplete: function(column) {
      return (column.columnTaken || this.availableSpaces(column) == 0)
    },
    columnActive: function(column) {
      if (column.columnTaken) {
        return false
      }

      return column.columnClaimedSpaces > 0
    },
    columnScore: function(column) {
      if (column.columnTaken || column.columnClaimedSpaces == 0) {
        return 0
      }
      return column.columnClaimedSpaces * column.columnPoints + column.columnPoints
    },
    buttonDisabled: function(boolean) {
      return {
        'opacity-50': boolean,
        'cursor-not-allowed': boolean
      }
    },
    toggleColumnTaken: function(column) {
      column.columnTaken = !column.columnTaken
    },
    placeMarker: function(column) {
      column.columnClaimedSpaces++
    },
    removeMarker: function(column) {
      column.columnClaimedSpaces--
    },
    availablePointsColour: function(column) {
      if (this.remainingMarkers == 0 && !this.columnActive(column) ) {
        return "text-gray-600"
      }

      return "text-gray-800"
    },
    availablePoints: function(column, index) {
      if (this.columnActive(column)) {
        return column.columnPoints
      }

      let lastRow = index == (this.availableSpaces(column) - 1)
      return lastRow ? column.columnPoints * 2 : column.columnPoints
    },
    resetBoard: function() {
      this.columns.map(column => this.resetColumn(column))
    },
    resetColumn: function(column) {
      column.columnTaken = false
      column.columnClaimedSpaces = 0
      column.columnOwnedSpaces = 0
    },
    saveProgress: function() {
      this.columns.map(column => this.saveColumn(column))
    },
    saveColumn: function(column) {
      column.columnOwnedSpaces = column.columnOwnedSpaces + column.columnClaimedSpaces
      column.columnClaimedSpaces = 0
    },
    wipeout: function() {
      this.columns.map(column => column.columnClaimedSpaces = 0)
    }
  }
}
</script>
